import { LoginTrackerEntity, UserTrackingDataResult } from '@/graphql/generated/graphql';
import { useAuthentication } from '@/hooks/useAuthentication';
import { getCurrentWeekDates } from '@/hooks/useCalendar';
import { fetchUserTracking, getLoginRange } from '@/services/auth/auth.service';
import cn from 'classnames';
import { useEffect, useState } from 'react';
import AnimatedNumber from '../AnimatedNumber';

type DayStatus = 'completed' | 'notCompleted' | 'current' | 'upcoming';

const statusBackground = {
  completed: '#C9F1DF',
  notCompleted: 'transparent',
  current: '#D3E5F6',
  upcoming: '#80A8E0',
};

interface Day {
  label: string;
  completed: boolean;
  status: DayStatus;
}

type Days = Day[];

const buildWeekDaysStatus = (week: Date[], loginTrackedWeek: LoginTrackerEntity[], referenceDay: Date) => {
  const trackedDates = loginTrackedWeek.map((entry) => entry!.attributes!.day);

  const referenceDateString = referenceDay.toISOString().slice(0, 10);

  return week.map((day) => {
    const dayString = day.toISOString().slice(0, 10);
    const label = day.toLocaleDateString('en-US', { weekday: 'short' }).charAt(0);
    let status: 'completed' | 'notCompleted' | 'current' | 'upcoming';

    if (dayString === referenceDateString) {
      status = 'current';
    } else if (trackedDates.includes(dayString)) {
      status = 'completed';
    } else if (day < referenceDay) {
      status = 'notCompleted';
    } else {
      status = 'upcoming';
    }

    return {
      label,
      completed: status === 'completed',
      status,
    };
  });
};

function DailyStreaks() {
  const { authentication } = useAuthentication();
  const [data, setData] = useState<UserTrackingDataResult | null | undefined>(null);
  const [weekDays, setWeekDays] = useState<Days | []>([]);

  useEffect(() => {
    async function getTrackingInfo() {
      try {
        const date = new Date();
        const week = getCurrentWeekDates(date);

        const [trackedData, loginTrackedWeek] = await Promise.all([
          fetchUserTracking(),
          getLoginRange(week[0], week[week.length - 1], authentication.user.id),
        ]);

        const days = buildWeekDaysStatus(week, loginTrackedWeek!, date) as Days;

        setData(trackedData);
        setWeekDays(days);
      } catch (error) {
        console.error('Error fetching user tracking data:', error);
      }
    }

    getTrackingInfo();
  }, [authentication]);

  if (!data) {
    return null;
  }

  return (
    <div className="flex flex-col justify-end gap-sh-30 rounded-[15px] border border-sh-new-blue p-5 md:flex-row">
      <div className="order-2 flex justify-between gap-x-sh-30 md:order-1">
        <div className="flex h-full flex-col items-center justify-center">
          <h3 className="mb-sh-5 whitespace-nowrap text-sh-11-14 font-bold">Current Streak</h3>
          <AnimatedNumber targetValue={data?.dailyStreak || 0} />
        </div>
        <div className="flex h-full flex-col items-center  justify-center">
          <h3 className="mb-sh-5  whitespace-nowrap text-sh-11-14 font-bold">Max Streak</h3>
          <AnimatedNumber targetValue={data?.streakRecord || 0} />
        </div>
        <div className="flex h-full flex-col items-center  justify-center">
          <h3 className="mb-sh-5  whitespace-nowrap text-sh-11-14 font-bold">Emotions tracked</h3>
          <AnimatedNumber targetValue={data?.emotionsTracked || 0} />
        </div>
      </div>

      <div className="order-1 flex h-full flex-col items-center justify-center md:order-2">
        <h3 className="mb-sh-5 whitespace-nowrap text-sh-11-14 font-bold">Your week</h3>
        <div className="flex flex-row justify-center">
          {weekDays.map((day, index) => (
            <div
              key={index}
              className={cn('mx-1 flex h-10 w-10 items-center justify-center rounded-full', {
                'mx-2 outline outline-2 outline-offset-4 outline-sh-new-blue-dark': day.status === 'current',
                'border-2 border-dashed border-primary-600 ': day.status === 'notCompleted',
              })}
              style={{
                backgroundColor: day.status !== 'current' ? statusBackground[day.status] : 'transparent',
              }}>
              <div
                className="flex h-full w-full items-center justify-center rounded-full"
                style={{ backgroundColor: statusBackground[day.status] }}>
                {day.completed ? (
                  <svg width="18" height="14" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M15.4098 0.299988L5.92016 9.78959L2.19056 6.07439L0.160156 8.10479L5.92016 13.8648L17.4402 2.34479L15.4098 0.299988Z"
                      fill="black"
                    />
                  </svg>
                ) : (
                  <span
                    className={cn('font-bol text-center text-gray-900', {
                      'opacity-25': day.status === 'notCompleted',
                    })}>
                    {day.label}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default DailyStreaks;
